
import React from 'react';
import { useFadeIn } from '../../../hooks/useFadeIn';

type PriceCardProps = {
  children: React.ReactNode;
  delay?: number;
};

function PriceCard({ children, delay = 0 }: PriceCardProps) {
  // Guard against the hook returning undefined (e.g., if the hook fails)
  const card = useFadeIn({ direction: 'up', delay }) ?? {
    ref: null,
    style: {},
  };

  return (
    <div ref={card.ref as React.RefObject<HTMLDivElement>} style={card.style}>
      {children}
    </div>
  );
}

export default function WhiteningTypes() {
  const heading = useFadeIn({ direction: 'up' }) ?? {
    ref: null,
    style: {},
  };

  return (
    <section className="py-32 px-6 lg:px-12 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div ref={heading.ref as React.RefObject<HTMLDivElement>} style={heading.style} className="mb-24">
          <h2 className="text-3xl lg:text-4xl text-gray-900 mb-12 leading-relaxed font-serif">
            料金・メニュー
          </h2>
        </div>

        <div className="space-y-16">
          {/* ホワイトニング */}
          <PriceCard delay={0}>
            <div className="bg-white rounded-2xl p-10 lg:p-12 shadow-sm border border-gray-100">
              <h3 className="text-2xl lg:text-3xl text-gray-900 mb-10 font-serif">ホワイトニング</h3>
              <div className="space-y-8">
                <div>
                  <p className="text-xs text-gray-500 mb-3 tracking-wider">内容</p>
                  <p className="text-gray-700">
                    ホワイトニング材と専用の照射器を併用し、<br />
                    歯の状態に合わせてホワイトニングを行います。
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-3 tracking-wider">費用（自費）</p>
                  <p className="text-gray-900 text-lg">19,900円〜118,800円（税込）</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-3 tracking-wider">期間・回数</p>
                  <p className="text-gray-700">
                    単品の場合：1日・1回<br />
                    コースの場合：1〜3か月・3〜5回<br />
                    （到達点の設定により異なります）
                  </p>
                </div>
                <div className="bg-amber-50 rounded-lg p-6 mt-8">
                  <p className="text-xs text-gray-600 mb-3 tracking-wider">副作用・リスク</p>
                  <p className="text-sm text-gray-700">
                    個人差はありますが、<br />
                    施術中や施術後に一時的に歯がしみる場合があります。
                  </p>
                </div>
              </div>
            </div>
          </PriceCard>

          {/* オーラルスパクリーニング */}
          <PriceCard delay={100}>
            <div className="bg-white rounded-2xl p-10 lg:p-12 shadow-sm border border-gray-100">
              <h3 className="text-2xl lg:text-3xl text-gray-900 mb-10 font-serif">オーラルスパクリーニング</h3>
              <div className="space-y-8">
                <div>
                  <p className="text-xs text-gray-500 mb-3 tracking-wider">内容</p>
                  <p className="text-gray-700">
                    歯石除去、フロッシング、PMTC、舌クリーニングなどを行い、<br />
                    歯周病予防やホワイトニング効果を高めて、お口の環境を最適化します。
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-3 tracking-wider">費用（自費）</p>
                  <p className="text-gray-900 text-lg">9,900円〜13,200円（税込）</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-3 tracking-wider">期間・回数</p>
                  <p className="text-gray-700">
                    通常1日・1回<br />
                    （お口の状態により異なります）
                  </p>
                </div>
                <div className="bg-amber-50 rounded-lg p-6 mt-8">
                  <p className="text-xs text-gray-600 mb-3 tracking-wider">副作用・リスク</p>
                  <p className="text-sm text-gray-700">
                    知覚過敏のある方は、<br />
                    刺激を感じる場合があります。
                  </p>
                </div>
              </div>
            </div>
          </PriceCard>

          {/* 返金保証 */}
          <PriceCard delay={150}>
            <div className="pt-8">
              <h3 className="text-xl lg:text-2xl text-gray-900 mb-8 font-serif">返金保証制度について</h3>
              <div className="space-y-6 text-gray-700">
                <p>
                  当院では、<br />
                  ホワイトニングが初めての方を対象に、<br />
                  初回施術後に変化を実感いただけなかった場合、<br />
                  翌日までのお申し出に対して<br />
                  施術費用を全額返金しています。
                </p>
                <p className="text-xs text-gray-500 mt-6">
                  ※制度の詳細は、来院時にご説明します
                </p>
              </div>
            </div>
          </PriceCard>

          {/* ホワイトエッセンスコース */}
          <PriceCard delay={200}>
            <div className="bg-stone-50 rounded-2xl p-10 lg:p-12 shadow-sm border border-gray-100 mt-24">
              <p className="text-xs text-gray-500 mb-4 tracking-wider">コースについて</p>
              <h3 className="text-2xl lg:text-3xl text-gray-900 mb-10 font-serif">ホワイトエッセンスコース</h3>
              <div className="space-y-8">
                <div>
                  <p className="text-xs text-gray-500 mb-3 tracking-wider">内容</p>
                  <p className="text-gray-700">
                    オフィスホワイトニング3回<br />
                    ホームホワイトニング2か月<br />
                    クリーニング2回
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-3 tracking-wider">費用（自費）</p>
                  <p className="text-gray-900 text-lg">118,800円（税込）</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-3 tracking-wider">期間</p>
                  <p className="text-gray-700">約6か月</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-6 mt-8">
                  <p className="text-xs text-gray-600 mb-3 tracking-wider">副作用・リスク</p>
                  <p className="text-sm text-gray-700">
                    個人差はありますが、<br />
                    施術中や施術後に歯がしみる場合があります。
                  </p>
                </div>
              </div>
            </div>
          </PriceCard>
        </div>
      </div>
    </section>
  );
}
